import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import {
  User, Wallet, Tag, Heart,
  Phone, Info, ShieldCheck, ClipboardList, Globe,
  Store, LogOut, ChevronLeft,
} from "lucide-react";

function MenuRow({ icon: Icon, label, value, to, onClick, danger, testId }) {
  const content = (
    <div
      className="flex items-center gap-3 bg-white rounded-2xl px-4 py-4 border border-[#F2F2F2] active:bg-[#FAFAFA] transition-colors"
      data-testid={testId}
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${danger ? "bg-[#FEF0EE]" : "bg-[#FEF0EE]"}`}>
        <Icon className={`w-5 h-5 ${danger ? "text-[#E05D3A]" : "text-[#E05D3A]"}`} strokeWidth={2.2} />
      </div>
      <span className={`flex-1 text-base font-semibold ${danger ? "text-[#E05D3A]" : "text-[#1A1A1A]"}`}>{label}</span>
      {value && <span className="text-sm font-bold text-[#E05D3A]">{value}</span>}
      <ChevronLeft className="w-5 h-5 text-[#E05D3A]" strokeWidth={2.5} />
    </div>
  );
  if (onClick) return <button type="button" onClick={onClick} className="w-full text-start">{content}</button>;
  return <Link to={to}>{content}</Link>;
}

export default function Profile() {
  const { user, logout } = useAuth();
  const { t, lang, toggleLang } = useLang();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/wallet")
      .then(({ data }) => setBalance(Number(data?.balance ?? 0)))
      .catch(() => setBalance(0));
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-5 py-10 text-center">
        <p className="text-[#6B6B6B] mb-4">{t("pleaseLogin") || "الرجاء تسجيل الدخول"}</p>
        <Link to="/login" className="inline-block rounded-full bg-[#E05D3A] text-white px-8 py-3 font-semibold">
          {t("login")}
        </Link>
      </div>
    );
  }

  const displayName = user.name?.split(" ")?.slice(0, 2)?.join(" ") || user.name;

  return (
    <div className="max-w-md mx-auto px-5 pt-6 pb-10 bg-[#FAFAFA] min-h-screen">
      {/* Greeting */}
      <h1 className="font-display text-3xl font-black text-[#1A1A1A] mb-6 text-center">
        {lang === "ar" ? `مرحبا ${displayName}` : `Hello ${displayName}`}
      </h1>

      <div className="space-y-2.5">
        <MenuRow icon={User}          label={lang === "ar" ? "حسابي" : "My account"} to="/profile/details" testId="menu-my-account" />
        <MenuRow icon={Wallet}        label={lang === "ar" ? "المحفظة" : "Wallet"} value={lang === "ar" ? `${(balance ?? 0).toFixed(2)} ﷼` : `SAR ${(balance ?? 0).toFixed(2)}`} to="/profile/wallet" testId="menu-wallet" />
        <MenuRow icon={Tag}           label={lang === "ar" ? "قسائمي" : "My coupons"} to="/profile/coupons" testId="menu-coupons" />
        <MenuRow icon={Heart}         label={lang === "ar" ? "المفضلة" : "Favorites"} to="/profile/favorites" testId="menu-favorites" />
        <MenuRow icon={Phone}         label={lang === "ar" ? "اتصل بنا" : "Contact us"} to="/profile/contact" testId="menu-contact" />
        <MenuRow icon={Info}          label={lang === "ar" ? "عن جايك" : "About Jayek"} to="/profile/about" testId="menu-about" />
        <MenuRow icon={ShieldCheck}   label={lang === "ar" ? "سياسة الخصوصية" : "Privacy policy"} to="/privacy" testId="menu-privacy" />
        <MenuRow icon={ClipboardList} label={lang === "ar" ? "حقوق والتزامات المستفيد" : "Rights & obligations"} to="/terms" testId="menu-terms" />
        <MenuRow icon={Globe}         label={lang === "ar" ? "تغيير اللغة" : "Change language"} value={lang === "ar" ? "English" : "العربية"} onClick={toggleLang} testId="menu-lang" />
        <MenuRow icon={Store}         label={lang === "ar" ? "أضِف علامتك التجارية معنا" : "Add your brand"} to="/profile/partner" testId="menu-partner" />
        <MenuRow icon={LogOut}        label={lang === "ar" ? "تسجيل الخروج" : "Log out"} onClick={handleLogout} danger testId="menu-logout" />
      </div>

      <p className="text-center text-xs text-[#8B8B8B] mt-8">
        {lang === "ar" ? "نسخة 1.0.0" : "Version 1.0.0"}
      </p>
    </div>
  );
}
